/**
 * ServiceOrderDetailsModal - Modal de detalhes da OS (Fase 6)
 *
 * Mesmo esqueleto do TaskDetailsModal (95vw x 97vh, tela cheia no celular,
 * cabeçalho compacto, trilha de fases compartilhada `PhaseTrail`, abas e
 * barra lateral de 360px). Os dados vêm de RPCs próprias da OS, porque o
 * modal de Tarefas está ligado à tabela de tarefas.
 *
 * RELEASE: 2026-10-09 v2.1.0 - Timeline operacional, aba Pausas e Histórico padronizado.
 * RELEASE: 2026-10-08 v1.0.0 - Versão inicial: Resumo + Andamentos, Histórico,
 *          ações de situação (BR-OS-003) e abas reservadas para as próximas fases.
 * RELEASE: 2026-10-08 v1.1.0 - Aba Checklists (Fase 8).
 * RELEASE: 2026-10-08 v1.2.0 - Fase 9: seleção de estoque em Aprovar e concluir e aba Produtos.
 * RELEASE: 2026-10-09 v1.3.0 - Resumo exibe Ponto de Referência, Telefone e link do Google Maps.
 * RELEASE: 2026-10-09 v2.0.0 - Visual igual ao modal de Tarefas: cabeçalho, abas, barra lateral e
 *          Resumo em cartões extraídos para `details/`; este arquivo só coordena dados e ações.
 * RELEASE: 2026-10-10 v2.2.0 - BR-OS-032: botão Agenda no cabeçalho com escolha entre
 *          iniciar sem agendamento ou agendar para um técnico (diálogo compartilhado).
 * RELEASE: 2026-10-10 v2.2.1 - BR-OS-032 (rev.): removido o diálogo de escolha; o botão
 *          Agenda abre direto o diálogo de agendamento (Iniciar já existe ao lado).
 * RELEASE: 2026-10-17 v2.3.0 - BR-OS-045: falta de prestador abre janela central de aviso
 *          ("Informar prestador" abre a edição) em vez de balão + edição automática.
 * RELEASE: 2026-10-17 v2.4.0 - BR-OS-047: aba Itens e janela "Serviços executados" antes do Concluir.
 * RELEASE: 2026-10-18 v2.5.0 - BR-OS-055: Resumo ganha subabas Geral | Indicadores.
 */
import { useMemo, useState } from 'react';
import { statusRequiresProvider, PROVIDER_REQUIRED_MESSAGE } from '@/lib/serviceOrders/providerRequirement';
import { useServiceOrderDetailsRealtime } from '@/hooks/useServiceOrderDetailsRealtime';
import { ServiceOrderModalHeader } from './details/ServiceOrderModalHeader';
import { ServiceOrderChangePhaseDialog } from './ServiceOrderChangePhase';
import { ServiceOrderModalTabs, type ServiceOrderModalTab } from './details/ServiceOrderModalTabs';
import { ServiceOrderModalSidebar } from './details/ServiceOrderModalSidebar';
import { ServiceOrderSummaryTab } from './details/ServiceOrderSummaryTab';
import { ServiceOrderActivityTab } from './details/ServiceOrderActivityTab';
import { ServiceOrderHistoryTab } from './details/ServiceOrderHistoryTab';
import { ServiceOrderPausesTab } from './details/ServiceOrderPausesTab';
import { ServiceOrderScheduleTab } from './details/ServiceOrderScheduleTab';
import { ServiceOrderScheduleDialog } from './details/ServiceOrderScheduleDialog';
import { useServiceOrderSchedules } from '@/hooks/useServiceOrderSchedules';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { InlineNotice } from '@/components/ui/inline-notice';
import { PhaseTrail } from '@/components/shared/PhaseTrail';
import { VehiclePlateChip } from '@/components/vehicles/VehiclePlateChip';
import { usePermissionsContext } from '@/contexts/PermissionsContext';
import { useTaskPhases } from '@/hooks/useTaskPhases';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { createLogger } from '@/lib/logger';
import { SERVICE_ORDER_STATUS_LABEL, isServiceOrderLocked, useMoveServiceOrderPhase,
  type ServiceOrderStatus } from '@/hooks/useServiceOrders';
import { getServiceOrderTransitions, useServiceOrderDetails,
  type ServiceOrderTimelineItem, type ServiceOrderTransition } from '@/hooks/useServiceOrderDetails';
import { TaskStatusPhaseDialog } from '@/components/tasks/modal/TaskStatusPhaseDialog';
import { useServiceOrderPhasePermissions } from '@/hooks/useServiceOrderPhasePermissions';
import { ServiceOrderFormDialog } from './ServiceOrderFormDialog';
import { ServiceOrderChecklistsTab } from './ServiceOrderChecklistsTab';
import { ServiceOrderMovementsTab } from './ServiceOrderMovementsTab';
import { ServiceOrderShipmentsTab } from './ServiceOrderShipmentsTab';
import { ServiceOrderStockDialog } from './ServiceOrderStockDialog';
import type { ServiceOrderStockSelection } from '@/hooks/useServiceOrderStock';
import { TooltipProvider } from '@/components/ui/tooltip';
import { useServiceOrderChecklists } from '@/hooks/useServiceOrderChecklists';
import { useServiceOrderStockMovements } from '@/hooks/useServiceOrderStock';
import { useServiceOrderPauses } from '@/hooks/useServiceOrderPauses';
import { useContactConversations } from '@/hooks/useContactConversations';
import { CustomerChatTab } from '@/components/crm/customers/modal/tabs/CustomerChatTab';
import { ChatConversationModal } from '@/components/tenant/kanban/ChatConversationModal';
import { useShipmentsList } from '@/hooks/useShipments';
import { buildServiceOrderTabCounts, hiddenServiceOrderTabs } from '@/lib/serviceOrderTabCounts';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { useAuth } from '@/hooks/useAuth';
import { useServiceOrderServiceMutations, useServiceOrderServices } from '@/hooks/useServiceOrderServices';
import { canConcludeWithServices, servicesRequiringAnswer } from '@/lib/serviceOrders/serviceItems';
import { ServiceOrderItemsTab } from './ServiceOrderItemsTab';
import { ServiceOrderServicesCompleteDialog } from './ServiceOrderServicesCompleteDialog';
import { ServiceOrderInsightsPanel } from './details/ServiceOrderInsightsPanel';
import { ModalSubTabs } from '@/components/shared/details-modal/ModalSubTabs';
import { BarChart3, LayoutDashboard } from 'lucide-react';

const logger = createLogger('ServiceOrderDetailsModal');

type Tab = ServiceOrderModalTab;

const msg = (e: unknown) => (e as { message?: string })?.message ?? 'Não foi possível concluir a ação';

interface Props {
  serviceOrderId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ServiceOrderDetailsModal({ serviceOrderId, open, onOpenChange }: Props) {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [tab, setTab] = useState<Tab>('activity');
  // BR-OS-055: subaba ativa dentro do Resumo
  const [summaryTab, setSummaryTab] = useState<'general' | 'insights'>('general');
  const [phaseDialogOpen, setPhaseDialogOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  // Aviso "Prestador obrigatório" (BR-OS-045)
  const [providerRequiredOpen, setProviderRequiredOpen] = useState(false);
  const [pending, setPending] = useState<ServiceOrderTransition | null>(null);
  const [note, setNote] = useState('');
  const [stockOpen, setStockOpen] = useState(false);
  // Revisado/Cancelar abrem o pop-up compartilhado de Tarefas (status + categoria/fase)
  const [statusPhase, setStatusPhase] = useState<ServiceOrderTransition | null>(null);
  // BR-OS-026: conversa aberta a partir da aba Chat
  const [chatConversationId, setChatConversationId] = useState<string | null>(null);
  // BR-OS-032 (rev.): botão Agenda do cabeçalho abre direto o diálogo de agendamento
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const { canDoOnPhase } = useServiceOrderPhasePermissions();
  const { details, timeline, addComment, updateComment, deleteComment, changeStatus, pauseTimer, resumeTimer } = useServiceOrderDetails(open ? serviceOrderId : null);
  // Situação mudada fora desta aba (remessa, outro usuário, portal) aparece sem F5
  useServiceOrderDetailsRealtime(serviceOrderId, open);
  const order = details.data;
  const { canEdit, isAdmin, canView } = usePermissionsContext() as any;
  // Logística: mesma consulta da aba Remessas (cache compartilhado) alimenta o contador
  const shipmentsQuery = useShipmentsList({
    search: '', status: null, carrierId: null, overdueOnly: false,
    entityType: 'service_order', entityId: serviceOrderId ?? '', page: 1, pageSize: 50,
  }, open && !!serviceOrderId && (isAdmin || canView('log_shipments')));
  const { phases } = useTaskPhases(order?.category_id, undefined, true) as any;
  const movePhase = useMoveServiceOrderPhase();
  const schedulesQuery = useServiceOrderSchedules(serviceOrderId ?? null);

  const locked = order ? isServiceOrderLocked(order.status) : true;
  const can = (key: string) => isAdmin || !!canEdit?.(key);
  const canEditOrder = can('os_list') || can('os_kanban');
  const isResponsible = order?.responsible_user_id === user?.id;
  const phaseCan = (action: Parameters<typeof canDoOnPhase>[1]) => canDoOnPhase(order?.phase_id, action, isResponsible);
  // Pode abrir a edição para informar o prestador (mesma regra do botão Editar)
  const canEditProvider = !locked && can('os_list') && phaseCan('can_edit');
  // Botões visíveis conforme situação + parâmetros do perfil (servidor revalida)
  const transitions = useMemo(
    () => (order ? getServiceOrderTransitions(order.status).filter((t) => canEditOrder && phaseCan('can_change_status') && (t.param !== 'os_param_reopen' || phaseCan('can_reopen')) && (!t.param || can(t.param))) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [order?.status, order?.phase_id, isResponsible, canDoOnPhase, canEditOrder, isAdmin],
  );

  const trailPhases = useMemo(
    () => ((phases ?? []) as any[]).filter((p) => p.is_active !== false)
      .map((p) => ({ id: p.id, name: p.name, color: p.color ?? null, sortOrder: p.sort_order ?? 0 })),
    [phases],
  );

  const runTransition = async (t: ServiceOrderTransition, n?: string, stock?: ServiceOrderStockSelection | null, phaseId?: string | null) => {
    // v1.4.0: o Concluir do prestador abre a seleção de itens (a movimentação ocorre nesse passo)
    if (t.needsStock && stock === undefined && order?.stock_movement && order.stock_movement !== 'none') {
      setStockOpen(true);
      return;
    }
    try {
      await changeStatus.mutateAsync({ status: t.to, note: n, stock, phaseId });
      toast.success(`OS: ${SERVICE_ORDER_STATUS_LABEL[t.to]}`);
      setPending(null); setNote(''); setStockOpen(false); setStatusPhase(null);
    } catch (e) { logger.error('Erro ao mudar situação', e); toast.error(msg(e)); }
  };

  const items = timeline.data ?? [];
  const comments = items.filter((i) => i.kind === 'comment');
  const events = items.filter((i) => i.kind === 'event');

  // BR-OS-026: totais das abas via as mesmas RPCs/queryKeys das abas (cache compartilhado)
  const activeId = open ? order?.id ?? null : null;
  const contactId = order?.customer_contact_id ?? null;
  const checklistsQuery = useServiceOrderChecklists(activeId);
  const stockQuery = useServiceOrderStockMovements(activeId, !!activeId);
  const pausesInfo = useServiceOrderPauses(activeId ?? '');
  const { data: conversations } = useContactConversations(contactId, { enabled: open && !!contactId });
  // BR-OS-047: serviços do negócio precisam de Sim/Não antes do Concluir
  const servicesQuery = useServiceOrderServices(activeId);
  const serviceMutations = useServiceOrderServiceMutations(activeId);
  const [servicesGate, setServicesGate] = useState<ServiceOrderTransition | null>(null);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        {/* Mesmas dimensões e comportamento do TaskDetailsModal */}
        <DialogContent preventCloseOnOutsideInteraction hideCloseButton
          className={cn('flex flex-col gap-0 overflow-hidden p-0',
            isMobile ? 'h-[100dvh] max-h-none w-screen max-w-none rounded-none border-0' : 'h-[97vh] w-[95vw] max-w-[95vw]')}>
          <VisuallyHidden.Root><DialogTitle>Detalhes da ordem de serviço</DialogTitle></VisuallyHidden.Root>
          {details.error ? (
            <div className="flex h-full flex-col items-center justify-center gap-4">
              <p className="text-muted-foreground">{msg(details.error)}</p>
              <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>Fechar</Button>
            </div>
          ) : details.isLoading || !order ? (
            /* Esqueleto idêntico ao de Tarefas */
            <div className="flex h-full flex-col">
              <div className="flex h-12 items-center gap-3 border-b px-4">
                <Skeleton className="h-5 w-16" /><Skeleton className="h-5 w-48 flex-1" />
                <div className="flex gap-1">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-8 w-8 rounded" />)}</div>
              </div>
              <div className="flex h-9 items-center gap-2 border-b px-4">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-5 w-20" />)}</div>
              <div className="flex min-h-0 flex-1">
                <div className="flex-1 space-y-4 p-4">
                  <div className="grid grid-cols-3 gap-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-20 rounded-lg" />)}</div>
                  <Skeleton className="h-48 rounded-lg" />
                </div>
                {!isMobile && <div className="w-[360px] space-y-4 border-l p-3"><Skeleton className="h-16 rounded-lg" /><Skeleton className="h-12" /><Skeleton className="h-32" /></div>}
              </div>
            </div>
          ) : (
            <TooltipProvider><div className="flex h-full min-h-0 flex-col">
              <ServiceOrderModalHeader order={order} transitions={transitions} busy={changeStatus.isPending}
                canEdit={!locked && can('os_list') && phaseCan('can_edit')}
                onTransition={(t) => {
                  // Prestador obrigatório para iniciar/revisar/concluir: abre aviso central antes do servidor recusar
                  if (statusRequiresProvider(t.to) && !order.provider_person_id) {
                    setProviderRequiredOpen(true);
                    return;
                  }
                  // BR-OS-047: Concluir com serviço do negócio sem resposta abre "Serviços executados"
                  if (t.to === 'awaiting_review' && !canConcludeWithServices(servicesQuery.data?.items ?? [])) {
                    setServicesGate(t);
                    return;
                  }
                  // Revisado (aguardando revisão → concluída) e Cancelar: pop-up com fase/categoria
                  if ((t.to === 'completed' && order.status === 'awaiting_review') || t.to === 'cancelled') return setStatusPhase(t);
                  return t.needsNote ? setPending(t) : runTransition(t);
                }}
                onEdit={() => setEditOpen(true)} onClose={() => onOpenChange(false)}
                onChangePhase={!locked && canEditOrder && phaseCan('can_change_phase') ? () => setPhaseDialogOpen(true) : undefined}
                // BR-OS-032 (rev.): Agenda abre direto o diálogo de agendamento (Iniciar já existe ao lado)
                onSchedule={!locked && canEditOrder && phaseCan('can_edit') ? () => setScheduleDialogOpen(true) : undefined}
                // Paridade com a aba Agenda: oculta o botão quando já há agendamento ativo
                hasActiveSchedule={(schedulesQuery.data ?? []).some((s) => s.status === 'active')} />
              {/* BR-OS-021: troca de categoria/fase */}
              <ServiceOrderChangePhaseDialog open={phaseDialogOpen} onOpenChange={setPhaseDialogOpen} orderId={order.id}
                currentCategoryId={order.category_id ?? null} currentPhaseId={order.phase_id ?? null}
                currentPhaseName={(order as any).phase_name ?? null} currentPhaseColor={(order as any).phase_color ?? null} />

              {/* BR-OS-032: diálogo de agendamento compartilhado com a aba Agenda */}
              <ServiceOrderScheduleDialog open={scheduleDialogOpen} onOpenChange={setScheduleDialogOpen}
                orderId={order.id} orderProviderId={order.provider_person_id} orderProviderName={order.provider_name} />

              {/* Trilha de fases compartilhada (mesma de Tarefas/Negócios) */}
              <PhaseTrail phases={trailPhases} currentPhaseId={order.phase_id}
                readOnly={locked || !canEditOrder || !phaseCan('can_change_phase') || movePhase.isPending}
                canMoveToPhase={phaseId => !locked && canEditOrder && phaseCan('can_change_phase') && canDoOnPhase(phaseId, 'can_change_phase', isResponsible)}
                onSelectPhase={(phaseId) => movePhase.mutate({ id: order.id, phaseId }, {
                  onSuccess: () => toast.success('Fase alterada'), onError: (e) => toast.error(msg(e)) })} />

              <ServiceOrderModalTabs activeTab={tab} onTabChange={setTab}
                hiddenTabs={hiddenServiceOrderTabs(contactId)}
                counts={buildServiceOrderTabCounts({
                  activity: items.length, history: events.length,
                  checklists: checklistsQuery.data?.length ?? 0, products: stockQuery.data?.length ?? 0,
                  pauses: pausesInfo.totalCount, chat: conversations?.length ?? 0,
                  scheduledStart: order.scheduled_start, status: order.status, schedules: schedulesQuery.data?.length,
                  shipments: shipmentsQuery.data?.total ?? 0,
                  items: servicesQuery.data?.items.length ?? 0,
                })} />

              <div className={cn('flex min-h-0 flex-1', isMobile ? 'flex-col' : 'flex-row')}>
                <div className="scrollbar-thin min-h-0 min-w-0 flex-1 overflow-y-auto p-2 md:p-4">
                  {tab === 'activity' && (
                    <ServiceOrderActivityTab orderId={order.id} comments={items} loading={timeline.isLoading} locked={locked || !phaseCan('can_comment')}
                      addComment={addComment} updateComment={updateComment} deleteComment={deleteComment} />
                  )}
                  {/* BR-OS-055: Resumo com subabas Geral | Indicadores */}
                  {tab === 'summary' && (
                    <div className="space-y-3">
                      {/* Mesmo visual da barra de abas da O.S. (ModalSubTabs) */}
                      <ModalSubTabs<'general' | 'insights'> active={summaryTab} onChange={setSummaryTab} className="-mx-2 -mt-2 md:-mx-4 md:-mt-4"
                        items={[{ id: 'general', label: 'Geral', icon: LayoutDashboard }, { id: 'insights', label: 'Indicadores', icon: BarChart3 }]} />
                      {summaryTab === 'general' ? (
                        <ServiceOrderSummaryTab order={order}
                          mobileSidebar={isMobile ? <ServiceOrderModalSidebar order={order} deal={servicesQuery.data?.deal ?? null} mobile pauseTimer={pauseTimer} resumeTimer={resumeTimer} /> : undefined} />
                      ) : <ServiceOrderInsightsPanel orderId={order.id} />}
                    </div>
                  )}
                  {tab === 'items' && <ServiceOrderItemsTab serviceOrderId={order.id} canEdit={!locked && canEditOrder && phaseCan('can_edit')} />}
                  {tab === 'pauses' && <ServiceOrderPausesTab serviceOrderId={order.id} />}
                  {/* BR-OS-026: conversas do contato principal do cliente (mesma aba de Tarefas/Veículos) */}
                  {tab === 'chat' && contactId && (
                    <CustomerChatTab contactId={contactId} contactName={order.customer_contact_name ?? order.customer_name ?? undefined}
                      onOpenConversation={(id) => setChatConversationId(id)} isActive={tab === 'chat'} />
                  )}
                  {tab === 'history' && <ServiceOrderHistoryTab events={events} loading={timeline.isLoading} />}
                  {/* Fase 8: checklists sequenciais (BR-OS-005) */}
                  {tab === 'checklists' && (
                    <ServiceOrderChecklistsTab
                      serviceOrderId={order.id}
                      orderStatus={order.status}
                      // BR-OS-033: liberação manual reaproveita a mutação oficial de situação
                      canReleaseStage={canEditOrder && phaseCan('can_change_status')}
                      onReleaseStage={() => changeStatus.mutateAsync({ status: order.status === 'in_correction' ? 'in_correction' : 'in_progress' })}
                    />
                  )}
                  {tab === 'products' && <ServiceOrderMovementsTab serviceOrderId={order.id} />}
                  {/* Logística Fase 4: remessas vinculadas à O.S. */}
                  {tab === 'shipments' && (
                    <ServiceOrderShipmentsTab serviceOrderId={order.id} serviceOrderNumber={order.number} status={order.status}
                      customer={order.customer_id ? { id: order.customer_id, name: order.customer_name ?? '' } : null} />
                  )}
                  {/* Fase 7: compromisso na agenda do prestador */}
                  {/* BR-OS-030: gestão de agendamentos (reagendar/cancelar) na própria aba */}
                  {tab === 'schedule' && <ServiceOrderScheduleTab order={order as any} canEdit={!locked && canEditOrder && phaseCan('can_edit')} />}
                </div>
                {!isMobile && (
                  <ServiceOrderModalSidebar order={order} deal={servicesQuery.data?.deal ?? null} pauseTimer={pauseTimer} resumeTimer={resumeTimer} />
                )}
              </div>
            </div></TooltipProvider>
          )}
        </DialogContent>
      </Dialog>
      {order && (
        <ServiceOrderStockDialog serviceOrderId={order.id} open={stockOpen} onOpenChange={setStockOpen} saving={changeStatus.isPending}
          onConfirm={(stock, n) => runTransition({ to: 'awaiting_review', label: 'Concluir', needsStock: true } as ServiceOrderTransition, n, stock)} />
      )}
      {/* BR-OS-047: respostas gravadas e, em seguida, o mesmo fluxo do Concluir */}
      <ServiceOrderServicesCompleteDialog open={!!servicesGate} onOpenChange={(o) => !o && setServicesGate(null)}
        items={servicesRequiringAnswer(servicesQuery.data?.items ?? [])} saving={serviceMutations.saveResults.isPending}
        confirmLabel="Salvar e concluir"
        onConfirm={async (payload) => {
          try {
            await serviceMutations.saveResults.mutateAsync(payload);
            const t = servicesGate; setServicesGate(null);
            if (t) await runTransition(t);
          } catch (e) { toast.error(msg(e)); }
        }} />
      {/* BR-OS-026: conversa sobreposta ao modal da OS (mesmo modal de Tarefas) */}
      <ChatConversationModal conversationId={chatConversationId} open={!!chatConversationId}
        onOpenChange={(o) => { if (!o) setChatConversationId(null); }} />
      <ServiceOrderFormDialog open={editOpen} onOpenChange={setEditOpen} serviceOrderId={serviceOrderId} />
      {order && (
        <TaskStatusPhaseDialog
          open={!!statusPhase}
          onOpenChange={(o) => !o && setStatusPhase(null)}
          action={statusPhase ? (statusPhase.to === 'cancelled' ? 'cancelled' : 'completed') : null}
          taskTitle={`${order.number} — ${order.service_type_name ?? ''}`}
          categoryId={order.category_id}
          currentPhaseId={order.phase_id}
          isServiceOrder
          requireNote={statusPhase?.to === 'cancelled'}
          canMoveToPhase={(phaseId) => canDoOnPhase(phaseId, 'can_change_phase', isResponsible)}
          isSubmitting={changeStatus.isPending}
          onConfirm={async ({ phaseId, completionNote }) => {
            if (statusPhase) await runTransition(statusPhase, completionNote ?? undefined, undefined, phaseId);
          }}
        />
      )}
      <AlertDialog open={!!pending} onOpenChange={(next) => { if (!next) { setPending(null); setNote(''); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{pending?.to === 'cancelled' ? 'Cancelar Ordem de Serviço?' : 'Devolver para correção?'}</AlertDialogTitle>
            <AlertDialogDescription>{pending?.to === 'cancelled' ? 'Informe o motivo do cancelamento.' : 'Informe o que precisa ser corrigido.'}</AlertDialogDescription>
          </AlertDialogHeader>
          <Textarea rows={4} maxLength={1000} value={note} onChange={(event) => setNote(event.target.value)} placeholder="Digite a observação obrigatória..." />
          <p className="text-right text-[10px] text-muted-foreground">{note.length}/1000</p>
          <AlertDialogFooter><AlertDialogCancel>Voltar</AlertDialogCancel><AlertDialogAction disabled={!note.trim() || changeStatus.isPending} onClick={(event) => { event.preventDefault(); if (pending) runTransition(pending, note.trim()); }} className={pending?.variant === 'destructive' ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : undefined}>Confirmar</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* BR-OS-045: aviso central quando falta o prestador (o balão no rodapé passava despercebido) */}
      <AlertDialog open={providerRequiredOpen} onOpenChange={setProviderRequiredOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Prestador obrigatório</AlertDialogTitle>
            <AlertDialogDescription>{PROVIDER_REQUIRED_MESSAGE}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Fechar</AlertDialogCancel>
            {canEditProvider && (
              <AlertDialogAction onClick={() => { setProviderRequiredOpen(false); setEditOpen(true); }}>Informar prestador</AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
